import dynamic from "next/dynamic";
import getConfig from "next/config";
import {
  makeObservable,
  computed,
  observable,
  reaction,
  action,
  runInAction,
} from "mobx";

import _get from "lodash/get";

import { OAuthProvider } from "common/OAuth/OAuth";
import type { Props as AuthProps } from "common/Auth/Auth";
import type { Props as ModalPromoCodeProps } from "common/ModalPromoCode/ModalPromoCode";

import { QueryResult } from "modules/graphql-fetch";
import { IStoreElement } from "types/store";
import { ExtendedNextPageContext } from "types/app";
import { runInBrowserOnly } from "helpers/common";
// import { formattedAddress } from "helpers/text";
import {
  appendServerResponseCookie,
  removeClientCookie,
  setClientCookie,
} from "helpers/cookies";
import { setAccessTokenToRequest } from "helpers/auth";
import {
  ACCESS_TOKEN_NAME,
  REFRESH_TOKEN_NAME,
  ACCESS_TOKEN_MAX_AGE,
  REFRESH_TOKEN_MAX_AGE,
} from "constants/variables";

const { publicRuntimeConfig } = getConfig();

const Auth = dynamic(() => import("common/Auth/Auth"), { ssr: false });

type WithPromoCodeProps = Partial<{
  props: Partial<ModalPromoCodeProps>;
  event: Event | React.MouseEvent;
}>;
const ModalPromoCode = dynamic(
  () => import("common/ModalPromoCode/ModalPromoCode"),
  {
    ssr: false,
  }
);

class User implements IStoreElement {
  private ctx?: ExtendedNextPageContext;
  @observable token: Entity.Token | null = null;
  @observable.shallow emias: Entity.Prescriptions | null = null;
  @observable profile: Entity.Profile | null = null;
  @observable isLoading = false;
  @observable lastStatusCard;
  @observable fetchedCardData = false;

  toJSON() {
    return {
      token: this.token?.refreshToken
        ? {
            accessToken: this.token?.accessToken || "",
            refreshToken: this.token?.refreshToken || "",
          }
        : null,
      profile: this.profile,
    };
  }

  constructor(api) {
    makeObservable(this); // https://mobx.js.org/enabling-decorators.html

    reaction(() => this.token, this.tokenReaction.bind(this));

    api.onRefreshToken(async (access, refresh) => {
      runInAction(() => {
        this.token = {
          accessToken: access,
          refreshToken: refresh,
        };
      });
    });

    api.onLogout(async () => {
      try {
        this.resetCookies();

        runInAction(() => {
          this.updateData({
            token: null,
            profile: null,
          });

          this.ctx.store.favorites.reset();
        });
      } catch (err) {
        console.error("Logout error", err);
      }
    });

    reaction(
      () => this.profile,
      () => {
        if (this.isAuth) {
          this.ctx.store.favorites.updateList(
            this.profile.favoriteProducts?.items || [],
            this.profile.favoriteProducts?.total || 0
          );
        }
        // обновляем регион при атворизации в сторе региона
        // авторизация может быть на сервере, может быть на клиенте.
        // INT-1405: Disabled it due to a bug
        // runInBrowserOnly(
        //   this.ctx.store.location.updateRegionFromProfile.bind(
        //     this.ctx.store.location
        //   )
        // )();
      }
    );
  }

  @action
  async tokenReaction() {
    if (!this.token) {
      await this.ctx.api.resetTokens();

      this.updateData({
        token: null,
        profile: null,
      });

      this.ctx.store.favorites.reset();

      this.resetCookies();
      return;
    }

    await this.ctx.api.setTokens({
      access: this.token.accessToken,
      refresh: this.token.refreshToken,
    });

    this.setCookies();
  }

  @computed
  get isAuth(): boolean {
    return !!this.token?.refreshToken && !!this.profile;
  }

  // @computed
  // get address(): Entity.Address | undefined {
  //   if (this.isAuth && this.profile?.addresses.length > 0) {
  //     return this.profile?.addresses[0];
  //   }
  // }

  // @computed
  // get deliveryAddress(): string {
  //   return formattedAddress(this.address, true);
  // }

  @computed
  get isEmployeeMode(): boolean {
    return Boolean(
      this.profile?.isEmployee && this.profile?.isEmployeeModeEnable
    );
  }

  @action
  async serverAuth(app: ExtendedNextPageContext): Promise<void> {
    const cookiesObj = _get(app, "req.headers.cookie", "")
      .replaceAll(" ", "")
      .split(";")
      .reduce((a, b) => {
        if (!b) return a;

        const i = b.split("=");
        a[i[0]] = i[1];
        return a;
      }, {});

    const tokenCookie = cookiesObj[ACCESS_TOKEN_NAME] || "";
    const refreshTokenCookie = cookiesObj[REFRESH_TOKEN_NAME] || "";

    if (refreshTokenCookie) {
      this.token = {
        accessToken: tokenCookie,
        refreshToken: refreshTokenCookie,
      };

      await this.ctx.api.setTokens({
        access: tokenCookie,
        refresh: refreshTokenCookie,
      });

      await this.fetchUser();
    }
  }

  @action
  async login(
    variables: Api.User.LoginInput
  ): Promise<QueryResult<Api.User.LoginResult>> {
    const { data, errors } = await this.ctx.api.methods.auth.login(variables);

    if (data) {
      runInAction(() => {
        this.updateData({
          token: data.login.token,
          profile: data.login.profile,
        });
      });
    }

    return { data, errors };
  }

  @action
  async register(
    variables: Api.User.RegisterInput
  ): Promise<QueryResult<Api.User.RegisterResult>> {
    const { data, errors } = await this.ctx.api.methods.auth.register(
      variables
    );

    if (data) {
      runInAction(() => {
        this.updateData({
          token: data.registerClient.token,
          profile: data.registerClient.profile,
        });
      });
    }

    return { data, errors };
  }

  @action
  async logout(): Promise<void> {
    const { data } = await this.ctx.api.methods.auth.logout();

    runInAction(() => {
      if (data) {
        this.updateData({
          token: null,
          profile: null,
        });

        this.ctx.store.favorites.reset();
      }
    });
  }

  @action
  async fetchPrescriptions(): Promise<void> {
    if (this.ctx.store.location.regionID === "moscowregion") {
      const {
        data: { prescriptions: data },
      } = await this.ctx.api.methods.products.prescriptions({
        regionID: this.ctx.store.location.regionID,
      });
      if (data) {
        runInAction(() => {
          this.emias = data;
        });
      }
    }
  }

  @action
  async fetchUser(): Promise<void> {
    const { data, errors } = await this.ctx.api.methods.profile.getProfile();

    runInAction(() => {
      if (errors) {
        this.updateData({
          token: null,
          profile: null,
        });
      }

      if (data) {
        this.profile = data.getProfile;
      }
    });
  }

  @action
  async updateProfile(
    variables: Api.User.UpdateProfileInput
  ): Promise<QueryResult<Api.User.UpdateProfileResult>> {
    const { data, errors } = await this.ctx.api.methods.profile.updateProfile(
      variables
    );

    if (data) {
      runInAction(() => {
        this.profile = data.updateProfile;
      });
    }

    return { data, errors };
  }

  @action
  async addPromotedEmail(
    variables: Api.User.AddPromotedEmail.Input
  ): Promise<QueryResult<Api.User.AddPromotedEmail.Result>> {
    const { data, errors } =
      await this.ctx.api.methods.profile.addPromotedEmail(variables);

    if (data?.addPromotedEmail) {
      runInAction(() => {
        this.profile = data.addPromotedEmail;
      });
    }

    return { data, errors };
  }

  // @computed
  // get genderForCard() {
  //   const gender = this.profile?.gender;
  //   if (gender) {
  //     switch (gender) {
  //       case "Муж.":
  //         return "male";
  //       case "Жен.":
  //         return "female";
  //     }
  //   }
  //   return "";
  // }

  @action
  updateData({ token, profile }: Entity.Session): void {
    this.token = token;
    this.profile = profile;
  }

  @action
  setProfile(profile: Entity.Profile): void {
    this.profile = profile;
  }

  updateLastStatusCard = (lastStatusCard: Entity.MindboxCard["status"]) => {
    this.lastStatusCard = lastStatusCard;
  };

  modalAuth = (props: Partial<AuthProps>): void => {
    this.ctx.store.notifications.show({
      type: "modal",
      component: Auth,
      props,
    });
  };

  setCookies(): void {
    if (typeof window === "undefined") {
      appendServerResponseCookie(this.ctx, {
        name: ACCESS_TOKEN_NAME,
        value: this.token.accessToken,
        path: "/",
        maxAge: ACCESS_TOKEN_MAX_AGE,
      });
      appendServerResponseCookie(this.ctx, {
        name: REFRESH_TOKEN_NAME,
        value: this.token.refreshToken,
        path: "/",
        maxAge: REFRESH_TOKEN_MAX_AGE,
      });

      setAccessTokenToRequest(this.ctx, this.token.accessToken);
    } else {
      setClientCookie(ACCESS_TOKEN_NAME, this.token.accessToken, {
        domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
        path: "/",
        maxAge: ACCESS_TOKEN_MAX_AGE,
      });
      setClientCookie(REFRESH_TOKEN_NAME, this.token.refreshToken, {
        domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
        path: "/",
        maxAge: REFRESH_TOKEN_MAX_AGE,
      });
    }
  }

  resetCookies(): void {
    if (typeof window === "undefined") {
      appendServerResponseCookie(this.ctx, {
        name: ACCESS_TOKEN_NAME,
        value: "",
        domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
        maxAge: -1,
      });

      appendServerResponseCookie(this.ctx, {
        name: REFRESH_TOKEN_NAME,
        value: "",
        domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
        maxAge: -1,
      });
    } else {
      removeClientCookie(ACCESS_TOKEN_NAME, {
        domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
        path: "/",
      });
      removeClientCookie(REFRESH_TOKEN_NAME, {
        domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
        path: "/",
      });
    }
  }

  withAuth = ({
    callback,
    props,
    event,
  }: Partial<{
    callback: () => void;
    props: Partial<AuthProps>;
    event: Event | React.MouseEvent;
  }>): void => {
    if (this.isAuth) {
      callback && callback.call(this);
      return;
    }

    event && event.preventDefault();
    this.modalAuth(props);
  };

  withPromoCode = ({ props, event }: WithPromoCodeProps = {}): void => {
    if (!this.isAuth) {
      this.withAuth({
        event,
        props: {
          promoCode: true,
        },
      });
      return;
    }

    this.ctx.store.notifications.show({
      type: "modal",
      component: ModalPromoCode,
      props,
    });
  };

  @action
  loginOAuth = async ({
    provider,
    code,
  }: {
    provider: OAuthProvider;
    code: string;
  }) => {
    const { data, errors } = await this.ctx.api.methods.auth.loginOAuth({
      oauthProvider: provider,
      code,
    });

    if (data?.loginOAuth.session) {
      this.updateData(data?.loginOAuth.session);
    }

    return { data, errors };
  };
}

export default User;
